import React, { useState, useEffect } from "react";
import api from "../axiosSetup";
import cfg from "../../config";
import AdminFormVorschau from "./AdminFormVorschau";
import {
    FELDTYPEN, typBezeichnung, brauchtOptionen, technischerName,
    optionenZuText, textZuOptionen, wertAusText,
} from "./feldtypen";

/**
 * Formularbaukasten je Typ.
 *
 * Gezeigt wird das vollstaendige Formular: die Felder, die fuer jeden Typ
 * gelten, sind grau hinterlegt und nur zur Ansicht da - bearbeitet werden
 * sie weiter oben. Dazwischen stehen die Felder dieses Typs.
 *
 * Jede Zeile zeigt zunaechst nur das Noetige. Bedingung, Auswahlmoeglich-
 * keiten und der technische Name liegen hinter "Mehr", damit die Liste auch
 * bei zwanzig Feldern lesbar bleibt.
 */

const BEREICHE = [
    {
        schluessel: "antrag",
        titel: "Antragsformular",
        hinweis: (typ) => `So sieht der Antrag aus, wenn jemand „${typ}" auswählt.`,
    },
    {
        schluessel: "ersatzteil",
        titel: "Ersatzteil-Formular",
        hinweis: (typ, term) =>
            `So sieht die Bestellung für ein ${term} vom Typ „${typ}" aus. `
            + `Ein Ja/Nein-Feld ohne Bedingung ist ein bestellbares Teil.`,
    },
];

// Steht im Auswahlfeld fuer "egal welcher Wert, Hauptsache ausgefuellt".
// Ein eigener Schluessel statt eines leeren Werts, damit er sich nicht mit
// einer echten Auswahlmoeglichkeit verwechseln laesst.
const NICHT_LEER = "__nicht_leer__";

const leeresFeld = () => ({
    name: "", label: "", type: "checkbox", required: false,
    optionenText: "", showIfField: "", showIfValue: "true",
    showIfMode: "equals", offen: true,
});

/**
 * @param nurBereich  "antrag" oder "ersatzteil" - zeigt nur diesen einen
 *                    Baukasten. Im Formular-Editor waere der jeweils andere
 *                    fehl am Platz.
 * @param sofortOffen Ohne den Zwischenschritt "bearbeiten": dort ist die
 *                    Auswahl des Typs schon der Aufklapper.
 */
const AdminCostumeTypeDefinition = ({ typId, typName, nurBereich, sofortOffen }) => {
    const bereiche = nurBereich
        ? BEREICHE.filter(b => b.schluessel === nurBereich)
        : BEREICHE;

    const [offen, setOffen]         = useState(!!sofortOffen);
    const [geladen, setGeladen]     = useState(false);
    const [felder, setFelder]       = useState({ antrag: [], ersatzteil: [] });
    const [rahmen, setRahmen]       = useState({
        antrag: { vor: [], nach: [] }, ersatzteil: { vor: [], nach: [] },
    });
    const [vorschau, setVorschau]   = useState({ antrag: false, ersatzteil: false });
    const [meldung, setMeldung]     = useState(null);
    const [speichert, setSpeichert] = useState(false);
    // Der zuletzt geladene Stand, um Ungespeichertes zu erkennen.
    const [urstand, setUrstand]     = useState(null);

    const kopf = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` } });

    const lade = async () => {
        try {
            const { data } = await api.get(`/api/costume-types/${typId}/definition`, kopf());
            const aufbereiten = (liste) => (liste || []).map(f => ({
                ...f,
                // Vom Server geladen: der technische Name steckt bereits in
                // gespeicherten Antraegen und darf sich nicht mehr aendern.
                bestehend: true,
                offen: false,
                optionenText: optionenZuText(f.options),
                showIfField: f.showIfField || "",
                showIfValue: f.showIfValue ?? "true",
                showIfMode: f.showIfMode || "equals",
            }));
            const antrag = aufbereiten(data.antrag);
            const ersatzteil = aufbereiten(data.ersatzteil);
            setFelder({ antrag, ersatzteil });
            setUrstand(JSON.stringify({
                antrag: hinaus(antrag, "antrag"),
                ersatzteil: hinaus(ersatzteil, "ersatzteil"),
            }));
            setRahmen(data.rahmen || { antrag: { vor: [], nach: [] }, ersatzteil: { vor: [], nach: [] } });
            setGeladen(true);
            setMeldung(null);
            return true;
        } catch {
            setMeldung({ art: "danger", text: "Die Felder konnten nicht geladen werden." });
            return false;
        }
    };

    // Wer sofort aufgeklappt startet, hat keinen Knopf, der das Laden
    // ausloest - und ein Wechsel des Typs muss die Felder neu holen.
    useEffect(() => {
        if (sofortOffen) { setGeladen(false); lade(); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [typId, sofortOffen]);

    /** Die Felder so, wie sie zum Server gehen - und wie sie verglichen werden. */
    const hinaus = (liste, bereich) => liste.map(f => ({
        name: f.name || technischerName(f.label, bereich),
        label: f.label,
        type: f.type,
        required: !!f.required,
        options: brauchtOptionen(f.type) ? textZuOptionen(f.optionenText) : null,
        showIfField: f.showIfField || null,
        showIfMode: f.showIfField ? (f.showIfMode || "equals") : "equals",
        showIfValue: (f.showIfField && f.showIfMode !== "notEmpty")
            ? String(f.showIfValue ?? "true") : null,
    }));

    // Die Vorschau zeigt den Bearbeitungsstand. Ob der schon gespeichert ist,
    // muss sie sagen koennen - sonst wirkt es, als kaeme die Aenderung im
    // echten Formular nicht an.
    const jetzigerStand = JSON.stringify({
        antrag: hinaus(felder.antrag, "antrag"),
        ersatzteil: hinaus(felder.ersatzteil, "ersatzteil"),
    });
    const nichtGespeichert = geladen && urstand !== null && jetzigerStand !== urstand;

    const umschalten = async () => {
        const neu = !offen;
        setOffen(neu);
        if (neu && !geladen) await lade();
    };

    const speichern = async () => {
        setSpeichert(true);
        setMeldung(null);

        try {
            await api.put(`/api/costume-types/${typId}/definition`, {
                antrag: hinaus(felder.antrag, "antrag"),
                ersatzteil: hinaus(felder.ersatzteil, "ersatzteil"),
            }, kopf());
            // Erst neu laden, dann melden: lade() raeumt die Meldung ab, die
            // Bestaetigung waere sonst sofort wieder verschwunden.
            if (await lade()) setMeldung({ art: "success", text: "Gespeichert." });
        } catch (err) {
            setMeldung({ art: "danger", text: err.response?.data?.message || "Speichern fehlgeschlagen." });
        } finally {
            setSpeichert(false);
        }
    };

    /**
     * Ein anderes ausloesendes Feld waehlen.
     *
     * Die Bedingungsart muss dazu passen: frueher wurde hier stur "true"
     * eingetragen - bei einem Zahlenfeld eine Bedingung, die niemals
     * zutrifft. Das Unterfeld blieb damit unsichtbar, ohne dass man den
     * Grund sah.
     */
    const setzeBedingungsfeld = (bereich, i, feldname) =>
        setFelder(vorher => {
            const liste = [...vorher[bereich]];
            const feld = liste[i];

            if (!feldname) {
                liste[i] = { ...feld, showIfField: "", showIfMode: "equals", showIfValue: "true" };
                return { ...vorher, [bereich]: liste };
            }

            const namen = (f) => f.name || technischerName(f.label, bereich);
            const quelle = liste.find(v => namen(v) === feldname);
            const antworten = quelle ? moeglicheAntworten(quelle) : [];
            const neu = {
                ...feld,
                showIfField: feldname,
                ...(antworten.length
                    ? { showIfMode: "equals", showIfValue: antworten[0].wert }
                    : { showIfMode: "notEmpty", showIfValue: "" }),
            };

            // Das Feld wandert unter seinen neuen Ausloeser. Bliebe es
            // stehen, zeigte die Liste es im Kasten eines fremden Feldes -
            // und die Bedingung koennte sogar nach oben ins Leere zeigen.
            liste.splice(i, 1);
            const quellIndex = liste.findIndex(f => namen(f) === feldname);
            if (quellIndex < 0) {
                liste.splice(i, 0, neu);
                return { ...vorher, [bereich]: liste };
            }
            liste.splice(endeDerGruppe(liste, quellIndex, bereich), 0, neu);
            return { ...vorher, [bereich]: liste };
        });

    /**
     * Die Auswahl "und zwar" fuehrt beides zusammen: die Bedingungsart und,
     * wenn es um einen bestimmten Wert geht, den Wert selbst.
     */
    const setzeBedingung = (bereich, i, wahl) =>
        setzeFeld(bereich, i, wahl === NICHT_LEER
            ? { showIfMode: "notEmpty", showIfValue: "" }
            : { showIfMode: "equals", showIfValue: wahl === "equals" ? "" : wahl });

    const setzeFeld = (bereich, i, aenderung) =>
        setFelder(vorher => ({
            ...vorher,
            [bereich]: vorher[bereich].map((f, idx) => (idx === i ? { ...f, ...aenderung } : f)),
        }));

    const entferne = (bereich, i) =>
        setFelder(vorher => {
            const weg = vorher[bereich][i];
            return {
                ...vorher,
                // Felder, die auf das entfernte verweisen, verlieren ihre
                // Bedingung - sonst zeigten sie ins Leere.
                [bereich]: vorher[bereich]
                    .filter((_, idx) => idx !== i)
                    .map(f => (f.showIfField === weg.name ? { ...f, showIfField: "" } : f)),
            };
        });

    /**
     * Legt direkt unter einem Feld ein Unterfeld an, das von ihm abhaengt.
     *
     * Ohne diesen Weg muss man das Unterfeld unten anhaengen, dort die
     * Bedingung suchen und es anschliessend nach oben schieben - der Grund,
     * warum abhaengige Felder bisher niemand gefunden hat.
     *
     * Der Wert kommt von aussen: bei Ja/Nein und Auswahl steht im Menue
     * schon, bei welcher Antwort das Unterfeld erscheinen soll.
     */
    const fuegeUnterfeldEin = (bereich, i, wert) =>
        setFelder(vorher => {
            const liste = [...vorher[bereich]];
            const quelle = liste[i];

            // Ans Ende der Gruppe, nicht direkt hinter das Feld: sonst
            // schoebe sich ein zweites Unterfeld zwischen das erste und
            // dessen eigene Unterfelder.
            liste.splice(endeDerGruppe(liste, i, bereich), 0, {
                ...leeresFeld(),
                // Ein Unterfeld ist selten wieder ein Haken - meist will man
                // etwas wissen, sobald angekreuzt wurde.
                type: "text",
                showIfField: quelle.name || technischerName(quelle.label, bereich),
                // Hat das ausloesende Feld keine festen Antworten - ein
                // Textfeld, eine Zahl -, ist "sobald etwas drinsteht"
                // gemeint. Ein leerer Vergleichswert haette das Unterfeld
                // nie erscheinen lassen.
                showIfMode: (wert === undefined && moeglicheAntworten(quelle).length === 0)
                    ? "notEmpty" : "equals",
                showIfValue: wert ?? (quelle.type === "checkbox" ? "true" : ""),
            });
            return { ...vorher, [bereich]: liste };
        });

    /**
     * Die Antworten, bei denen ein Unterfeld erscheinen kann. Leer, wenn das
     * Feld keine festen Antworten hat - dann wird der Wert von Hand
     * eingetragen.
     */
    const moeglicheAntworten = (f) => {
        if (f.type === "checkbox") {
            return [
                { wert: "true", text: "wenn angehakt" },
                { wert: "false", text: "wenn nicht angehakt" },
            ];
        }
        if (brauchtOptionen(f.type)) {
            return textZuOptionen(f.optionenText)
                .map(o => ({ wert: o.value, text: `wenn „${o["frontend-text"]}"` }));
        }
        return [];
    };

    const verschiebe = (bereich, i, richtung) =>
        setFelder(vorher => {
            const liste = [...vorher[bereich]];
            const ziel = i + richtung;
            if (ziel < 0 || ziel >= liste.length) return vorher;
            [liste[i], liste[ziel]] = [liste[ziel], liste[i]];
            return { ...vorher, [bereich]: liste };
        });

    /**
     * Wie tief ein Feld haengt: null fuer eigenstaendige, eins fuer die, die
     * an einem daruberliegenden haengen, und so weiter. Daraus wird die
     * Einrueckung - wer die Liste ansieht, soll die Staffelung erkennen,
     * ohne jede Bedingung einzeln zu lesen.
     */
    const berechneTiefen = (liste, bereich) => {
        const tiefen = [];
        const zeileZuName = new Map();
        liste.forEach((f, i) => {
            const eltern = f.showIfField ? zeileZuName.get(f.showIfField) : undefined;
            tiefen[i] = eltern === undefined ? 0 : tiefen[eltern] + 1;
            const eigen = f.name || technischerName(f.label, bereich);
            if (eigen) zeileZuName.set(eigen, i);
        });
        return tiefen;
    };

    /**
     * Fasst jedes eigenstaendige Feld mit allem zusammen, was daran haengt.
     * Eingerueckt allein reichte nicht - ein eigener Rahmen ums Unterfeld
     * liess es getrennt wirken statt zugehoerig.
     */
    const bildeGruppen = (liste, tiefen, bereich) => {
        const namen = (f) => f.name || technischerName(f.label, bereich);
        const gruppen = [];
        let inGruppe = new Set();

        liste.forEach((f, i) => {
            // Nur wer an einem Feld *dieser* Gruppe haengt, gehoert dazu. Ein
            // Feld, dessen Ausloeser weiter oben in einer anderen Gruppe
            // steht, beginnt eine eigene - sonst behauptete der Rahmen eine
            // Zusammengehoerigkeit, die es nicht gibt.
            const gehoertDazu = tiefen[i] > 0 && f.showIfField && inGruppe.has(f.showIfField);
            if (gehoertDazu) {
                gruppen[gruppen.length - 1].push(i);
            } else {
                gruppen.push([i]);
                inGruppe = new Set();
            }
            const eigen = namen(f);
            if (eigen) inGruppe.add(eigen);
        });
        return gruppen;
    };

    /** Wo die Felder enden, die (auch mittelbar) an diesem haengen. */
    const endeDerGruppe = (liste, start, bereich) => {
        const namen = (f) => f.name || technischerName(f.label, bereich);
        const dazu = new Set([namen(liste[start])]);
        let stelle = start + 1;
        while (stelle < liste.length
               && liste[stelle].showIfField
               && dazu.has(liste[stelle].showIfField)) {
            dazu.add(namen(liste[stelle]));
            stelle++;
        }
        return stelle;
    };

    /**
     * Ob zwei Felder die Plaetze tauschen duerfen. Eine Bedingung darf nur
     * nach oben zeigen; der Server weist alles andere ab. Lieber den Pfeil
     * sperren, als den Fehler erst beim Speichern zu zeigen.
     */
    const tauschMoeglich = (liste, i, ziel, bereich) => {
        if (ziel < 0 || ziel >= liste.length) return false;
        const neu = [...liste];
        [neu[i], neu[ziel]] = [neu[ziel], neu[i]];
        const namen = (f) => f.name || technischerName(f.label, bereich);
        const vorhanden = new Set(neu.map(namen).filter(Boolean));
        const bisher = new Set();
        for (const f of neu) {
            // Eine Bedingung auf ein geloeschtes Feld bleibt aussen vor -
            // sie wuerde sonst jeden Tausch sperren.
            if (f.showIfField && vorhanden.has(f.showIfField) && !bisher.has(f.showIfField)) {
                return false;
            }
            const eigen = namen(f);
            if (eigen) bisher.add(eigen);
        }
        return true;
    };

    /** Die Felder eines Bereichs so, wie das Formular sie zeigt. */
    const vollstaendigeFelder = (bereich) => {
        const umgebung = rahmen[bereich] || { vor: [], nach: [] };
        const eigene = felder[bereich].map(f => ({
            name: f.name || technischerName(f.label, bereich),
            label: f.label || "(ohne Beschriftung)",
            type: f.type,
            required: !!f.required,
            options: brauchtOptionen(f.type) ? textZuOptionen(f.optionenText) : null,
            showIf: f.showIfField
                ? (f.showIfMode === "notEmpty"
                    ? { field: f.showIfField, notEmpty: true }
                    : { field: f.showIfField, equals: wertAusText(String(f.showIfValue)) })
                : null,
        }));
        // Hier geht es um genau einen Typ. Also steht in der Typauswahl auch
        // nur dieser - sonst bliebe sie in der Vorschau leer und gesperrt,
        // und nichts, was am Typ haengt, liesse sich ausprobieren.
        const nurDieserTyp = (f) => (f?.type === "costume-type-select"
            ? { ...f, options: [{ value: typName, "frontend-text": typName }] }
            : f);
        return [
            ...umgebung.vor.map(nurDieserTyp),
            ...eigene,
            ...umgebung.nach.map(nurDieserTyp),
        ];
    };

    if (!offen) {
        return (
            <button className="btn btn-sm btn-outline-secondary" onClick={umschalten}>
                Anträge &amp; Ersatzteile bearbeiten
            </button>
        );
    }

    /** Ein Feld, das fuer alle Typen gilt: nur zur Ansicht. */
    const allgemeineZeile = (f, i) => (
        <div className="list-group-item bg-body-secondary py-2" key={`allgemein-${i}`}>
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <span className="text-muted">
                    {f.label || f.name}
                    {f.required && <span className="text-danger ms-1">*</span>}
                    <span className="badge bg-light text-dark ms-2">{typBezeichnung(f.type)}</span>
                </span>
                <span className="badge bg-secondary">gilt für alle Typen</span>
            </div>
        </div>
    );

    const feldZeile = (bereich, f, i, liste, tiefe) => {
        const moegliche = liste.slice(0, i).filter(v => v.name || v.label);
        const bedingungsFeld = moegliche.find(v => (v.name || v.label) === f.showIfField);
        const istTeil = bereich === "ersatzteil" && f.type === "checkbox" && !f.showIfField;

        const bedingungsText = !f.showIfField ? null
            : f.showIfMode === "notEmpty"
                ? `nur wenn „${bedingungsFeld?.label || f.showIfField}" ausgefüllt ist`
                : `nur wenn „${bedingungsFeld?.label || f.showIfField}" = ${
                    bedingungsFeld?.type === "checkbox"
                        ? (String(f.showIfValue) === "true" ? "ja" : "nein")
                        : `„${f.showIfValue}"`}`;

        // Tiefer als vier Stufen wird nicht weiter eingerueckt, sonst bleibt
        // auf dem Telefon keine Breite mehr fuer die Felder selbst.
        const einzug = Math.min(tiefe, 4) * 1.5;

        // Ohne Beschriftung gibt es keinen Namen, auf den ein Unterfeld
        // verweisen koennte.
        const benennbar = !!(f.label || f.name);
        const antworten = moeglicheAntworten(f);

        return (
            // border-primary faerbte alle vier Seiten und liess das Unterfeld
            // wie einen eigenen Kasten aussehen - genau der falsche Eindruck.
            // Die Kante kommt deshalb nur links, und im hellen Ton: kraeftig
            // verschmilzt sie mit dem Rahmen, der die Gruppe zusammenhaelt.
            <div className="list-group-item py-2" data-feld=""
                style={{
                    ...(einzug ? { marginLeft: `${einzug}rem` } : {}),
                    ...(tiefe > 0
                        ? { borderLeft: '3px solid var(--bs-primary-border-subtle)' }
                        : {}),
                }}
                key={i}>
                {(bedingungsText || istTeil) && (
                    <div className="small mb-1 d-flex flex-wrap gap-2 align-items-center">
                        {istTeil && (
                            <span className="badge bg-warning text-dark">bestellbares Teil</span>
                        )}
                        {bedingungsText && (
                            <span className="badge bg-primary-subtle text-primary-emphasis border border-primary-subtle">
                                ↳ {bedingungsText}
                            </span>
                        )}
                    </div>
                )}
                {/* Kopfzeile: Beschriftung, Art, Aktionen. Auf schmalen
                    Bildschirmen stapeln sich die Spalten. */}
                <div className="row g-2 align-items-center">
                    <div className="col-12 col-lg-4">
                        <input className="form-control form-control-sm"
                            placeholder="Beschriftung, z. B. Schuhgröße"
                            value={f.label || ""}
                            onChange={e => {
                                const wert = e.target.value;
                                setzeFeld(bereich, i, {
                                    label: wert,
                                    name: f.bestehend ? f.name : technischerName(wert, bereich),
                                });
                            }} />
                    </div>
                    <div className="col-7 col-lg-3">
                        <select className="form-select form-select-sm" value={f.type}
                            onChange={e => setzeFeld(bereich, i, { type: e.target.value })}>
                            {FELDTYPEN.map(t => <option key={t.wert} value={t.wert}>{t.text}</option>)}
                        </select>
                    </div>
                    <div className="col-5 col-lg-1">
                        <div className="form-check mb-0 text-nowrap">
                            <input className="form-check-input" type="checkbox" checked={!!f.required}
                                id={`pflicht-${bereich}-${i}`}
                                onChange={e => setzeFeld(bereich, i, { required: e.target.checked })} />
                            <label className="form-check-label small" htmlFor={`pflicht-${bereich}-${i}`}>
                                Pflicht
                            </label>
                        </div>
                    </div>
                    <div className="col-12 col-lg-4 d-flex justify-content-lg-end gap-1 flex-wrap">
                        {antworten.length > 0 ? (
                            // Feste Antworten: dann steht schon im Menue, bei
                            // welcher das Unterfeld erscheinen soll.
                            <div className="btn-group">
                                <button className="btn btn-sm btn-outline-primary dropdown-toggle"
                                    data-bs-toggle="dropdown" disabled={!benennbar}
                                    title={benennbar
                                        ? `Feld anlegen, das unter „${f.label || f.name}" erscheint`
                                        : "Erst eine Beschriftung eintragen"}>
                                    + Unterfeld
                                </button>
                                <ul className="dropdown-menu dropdown-menu-end">
                                    {antworten.map(a => (
                                        <li key={a.wert}>
                                            <button className="dropdown-item"
                                                onClick={() => fuegeUnterfeldEin(bereich, i, a.wert)}>
                                                {a.text}
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ) : (
                            <button className="btn btn-sm btn-outline-primary"
                                disabled={!benennbar}
                                onClick={() => fuegeUnterfeldEin(bereich, i)}
                                title={benennbar
                                    ? `Feld anlegen, das unter „${f.label || f.name}" erscheint`
                                    : "Erst eine Beschriftung eintragen"}>
                                + Unterfeld
                            </button>
                        )}
                        {/* Gruppiert, damit bei wenig Platz nicht mitten
                            zwischen zwei zusammengehoerenden Knoepfen
                            umgebrochen wird. */}
                        <div className="btn-group">
                            <button className="btn btn-sm btn-outline-secondary"
                                disabled={!tauschMoeglich(liste, i, i - 1, bereich)}
                                onClick={() => verschiebe(bereich, i, -1)}
                                title="nach oben">↑</button>
                            <button className="btn btn-sm btn-outline-secondary"
                                disabled={!tauschMoeglich(liste, i, i + 1, bereich)}
                                onClick={() => verschiebe(bereich, i, 1)}
                                title="nach unten">↓</button>
                        </div>
                        <div className="btn-group">
                            <button className="btn btn-sm btn-outline-secondary"
                                onClick={() => setzeFeld(bereich, i, { offen: !f.offen })}>
                                {f.offen ? "Weniger" : "Mehr"}
                            </button>
                            <button className="btn btn-sm btn-outline-danger"
                                onClick={() => entferne(bereich, i)} title="entfernen">✕</button>
                        </div>
                    </div>
                </div>

                {f.offen && (
                    <div className="mt-2 ps-2 border-start">
                        {istTeil && (
                            <p className="small text-muted mb-2">
                                Ohne Bedingung ist dieses Ja/Nein-Feld ein Teil, das sich bestellen lässt.
                            </p>
                        )}

                        <div className="row g-2 align-items-end">
                            <div className="col-12 col-md-6">
                                <label className="form-label small mb-1">Nur anzeigen, wenn</label>
                                <select className="form-select form-select-sm" value={f.showIfField || ""}
                                    onChange={e => setzeBedingungsfeld(bereich, i, e.target.value)}>
                                    <option value="">
                                        {bereich === "ersatzteil"
                                            ? "— immer (eigenständiges Teil)"
                                            : "— immer, sobald der Typ gewählt ist"}
                                    </option>
                                    {moegliche.map(v => (
                                        <option key={v.name || v.label} value={v.name || v.label}>
                                            {v.label || v.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            {f.showIfField && (
                                <div className="col-12 col-md-6">
                                    <label className="form-label small mb-1">
                                        {bedingungsFeld?.type === "checkbox" ? "den Wert hat" : "und zwar"}
                                    </label>
                                    {bedingungsFeld?.type === "checkbox" ? (
                                        <select className="form-select form-select-sm" value={String(f.showIfValue)}
                                            onChange={e => setzeFeld(bereich, i, { showIfValue: e.target.value })}>
                                            <option value="true">ja / angehakt</option>
                                            <option value="false">nein</option>
                                        </select>
                                    ) : bedingungsFeld && brauchtOptionen(bedingungsFeld.type) ? (
                                        // Bei einer Auswahl ist "irgendetwas gewaehlt" genauso
                                        // haeufig gemeint wie eine bestimmte Moeglichkeit.
                                        <select className="form-select form-select-sm"
                                            value={f.showIfMode === "notEmpty" ? NICHT_LEER : String(f.showIfValue)}
                                            onChange={e => setzeBedingung(bereich, i, e.target.value)}>
                                            <option value={NICHT_LEER}>— irgendeine Auswahl getroffen —</option>
                                            {textZuOptionen(bedingungsFeld.optionenText).map(o => (
                                                <option key={o.value} value={o.value}>{o["frontend-text"]}</option>
                                            ))}
                                        </select>
                                    ) : (
                                        // Textfeld, Zahl, Datum: auf jeden denkbaren Wert einzeln
                                        // zu zeigen, ergibt keinen Sinn - gemeint ist fast immer
                                        // "sobald etwas eingetragen wurde".
                                        <div className="d-flex gap-2 flex-wrap">
                                            <select className="form-select form-select-sm" style={{ maxWidth: "14rem" }}
                                                value={f.showIfMode === "notEmpty" ? NICHT_LEER : "equals"}
                                                onChange={e => setzeBedingung(bereich, i, e.target.value)}>
                                                <option value={NICHT_LEER}>ist ausgefüllt</option>
                                                <option value="equals">hat genau den Wert…</option>
                                            </select>
                                            {f.showIfMode !== "notEmpty" && (
                                                <input className="form-control form-control-sm" style={{ minWidth: "8rem" }}
                                                    placeholder="Wert"
                                                    value={String(f.showIfValue ?? "")}
                                                    onChange={e => setzeFeld(bereich, i, { showIfValue: e.target.value })} />
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {brauchtOptionen(f.type) && (
                            <div className="mt-2">
                                <label className="form-label small mb-1">
                                    Auswahlmöglichkeiten — eine je Zeile
                                </label>
                                <textarea className="form-control form-control-sm" rows={3}
                                    placeholder={"Holz-Schere = schere\nKorb"}
                                    value={f.optionenText || ""}
                                    onChange={e => setzeFeld(bereich, i, { optionenText: e.target.value })} />
                                <div className="form-text">
                                    Steht links und rechts vom Gleichheitszeichen etwas Verschiedenes,
                                    wird links angezeigt und rechts gespeichert. Ohne Zeichen ist beides gleich.
                                </div>
                            </div>
                        )}

                        <div className="form-text mt-2">Technisch: <code>{f.name || "—"}</code></div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className={sofortOffen ? "" : "mt-3 border-top pt-3"} data-typbaukasten={typId}>
            {!sofortOffen && (
                <>
                    <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                        <strong>Formulare für „{typName}"</strong>
                        <button className="btn btn-sm btn-outline-secondary" onClick={umschalten}>Zuklappen</button>
                    </div>
                    <p className="text-muted small">
                        Hier steht das vollständige Formular. Die grau hinterlegten Felder gelten für
                        jeden Typ und werden oben bearbeitet. Alles dazwischen gehört allein zu „{typName}".
                    </p>
                </>
            )}

            {meldung && <div className={`alert alert-${meldung.art} py-2`}>{meldung.text}</div>}

            {bereiche.map(bereich => {
                const umgebung = rahmen[bereich.schluessel] || { vor: [], nach: [] };
                const eigene = felder[bereich.schluessel];
                return (
                    <div key={bereich.schluessel} className="mb-4">
                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                            <h6 className="mb-0">{bereich.titel}</h6>
                            <button className="btn btn-sm btn-outline-info"
                                onClick={() => setVorschau(v => ({ ...v, [bereich.schluessel]: true }))}>
                                Vorschau
                                {nichtGespeichert && (
                                    <span className="badge bg-warning text-dark ms-2">ungespeichert</span>
                                )}
                            </button>
                        </div>
                        <p className="text-muted small mb-2">
                            {bereich.hinweis(typName, cfg.costumeTerm)}
                            {" "}Soll ein Feld erst nach einer bestimmten Antwort auftauchen, ist
                            das ein <em>zweites</em> Feld: „+ Unterfeld" hinter dem auslösenden
                            legt es an und trägt die Bedingung gleich ein. Bei Ja/Nein und
                            Auswahl steht im Menü schon, bei welcher Antwort.
                        </p>

                        <AdminFormVorschau
                            offen={!!vorschau[bereich.schluessel]}
                            onSchliessen={() => setVorschau(v => ({ ...v, [bereich.schluessel]: false }))}
                            nichtGespeichert={nichtGespeichert}
                            felder={vollstaendigeFelder(bereich.schluessel)}
                            titel={`${bereich.titel} — Vorschau für „${typName}"`} />

                        <div className="list-group mb-2">
                            {umgebung.vor.map(allgemeineZeile)}
                            {eigene.length === 0 && (
                                <div className="list-group-item text-muted small">
                                    Noch keine eigenen Felder für diesen Typ.
                                </div>
                            )}
                            {(() => {
                                const tiefen = berechneTiefen(eigene, bereich.schluessel);
                                const zeile = (i) =>
                                    feldZeile(bereich.schluessel, eigene[i], i, eigene, tiefen[i]);
                                return bildeGruppen(eigene, tiefen, bereich.schluessel).map(gruppe => {
                                    // Ein Feld ohne Anhang bleibt eine schlichte Zeile.
                                    if (gruppe.length === 1) return zeile(gruppe[0]);
                                    return (
                                        <div className="list-group-item p-0 border-primary"
                                            key={`gruppe-${gruppe[0]}`}>
                                            <div className="list-group list-group-flush">
                                                {gruppe.map(zeile)}
                                            </div>
                                            <div className="small text-muted px-3 py-1 border-top">
                                                Diese {gruppe.length} Felder gehören zusammen — die
                                                eingerückten erscheinen erst, wenn die Bedingung zutrifft.
                                            </div>
                                        </div>
                                    );
                                });
                            })()}
                            {umgebung.nach.map(allgemeineZeile)}
                        </div>

                        <button className="btn btn-sm btn-outline-primary"
                            onClick={() => setFelder(vorher => ({
                                ...vorher,
                                [bereich.schluessel]: [...vorher[bereich.schluessel], leeresFeld()],
                            }))}>
                            + Feld hinzufügen
                        </button>
                    </div>
                );
            })}

            <div className="mt-3 d-flex gap-2 flex-wrap">
                <button className="btn btn-primary" onClick={speichern} disabled={speichert}>
                    {speichert ? "Speichern…" : "Speichern"}
                </button>
                <button className="btn btn-outline-secondary" onClick={lade} disabled={speichert}>
                    Änderungen verwerfen
                </button>
            </div>
        </div>
    );
};

export default AdminCostumeTypeDefinition;
